import GenericPopup from '@components/Popup/GenericPopup';
import {
  createContext,
  ReactNode,
  ReactElement,
  useState,
  useCallback,
} from 'react';

interface IGenericPopupContext {
  isOpen: boolean;
  popupType: string;
  showPopup: (popupContent?: GenericPopupContent) => void;
  showWrongNetworkPopup: () => void;
  closePopup: () => void;
}

export interface GenericPopupContent {
  header: ReactNode;
  body: ReactNode;
}

export const GenericPopupContext = createContext({} as IGenericPopupContext);

const defaultContent: GenericPopupContent = {
  header: 'Something went wrong',
  body: 'Please try again later',
};

const GenericPopupProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [content, setContent] = useState(defaultContent);
  const [popupType, setPopupType] = useState('');

  const showPopup = useCallback(
    (
      popupContent: GenericPopupContent = defaultContent,
      popupType: string = ''
    ) => {
      setContent(popupContent);
      setIsOpen(true);
      setPopupType(popupType);
    },
    [setIsOpen, setContent, setPopupType]
  );

  const showWrongNetworkPopup = useCallback(() => {
    showPopup(
      {
        header: 'Incorrect Wallet Network',
        body: 'Please change your active wallet network to match your desired origin chain to proceed.',
      },
      'WRONG_NETWORK_ERROR'
    );
  }, [showPopup]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
    setPopupType('');
  }, [setIsOpen, setPopupType]);

  return (
    <GenericPopupContext.Provider
      value={{
        isOpen,
        popupType,
        showPopup,
        showWrongNetworkPopup,
        closePopup,
      }}
    >
      {children}
      <GenericPopup
        isOpen={isOpen}
        headerContent={content.header}
        bodyContent={content.body}
        onClose={() => setIsOpen(false)}
      />
    </GenericPopupContext.Provider>
  );
};

export default GenericPopupProvider;
